import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '@/services/api'
import { useSizeConfigs } from '@/hooks/useProducts'
import { useAzurePricing } from '@/hooks/usePricing'
import {
  ChevronRight, ChevronLeft, Check, AlertCircle, Copy, CheckCircle,
  Server, Database, Network, Settings, Package, FileText, Rocket,
  Plus, Trash2, Info,
} from 'lucide-react'
import styles from './ProductOnboardingPage.module.css'

// ─── Step Definitions ───────────────────────────────────────────────────────

const STEPS = [
  { number: 1, label: 'Product Information',        desc: 'Product, environment & scale',   icon: Package },
  { number: 2, label: 'Infrastructure Sizing',      desc: 'Auto-calculated compute tier',    icon: Settings },
  { number: 3, label: 'Resource Group & Subscription', desc: 'Azure placement',              icon: Server },
  { number: 4, label: 'Networking Configuration',   desc: 'VNet & subnet selection',         icon: Network },
  { number: 5, label: 'Database Configuration',     desc: 'Azure SQL provisioning',          icon: Database },
  { number: 6, label: 'Virtual Machine Configuration', desc: 'VM identity & OS',             icon: Server },
  { number: 7, label: 'Review & Submit',            desc: 'Confirm & provision',             icon: FileText },
]

// ─── Data Constants ─────────────────────────────────────────────────────────

const REGION_MAP_PROD: Record<string, { rg: string; vnet: string; sql: string | null }> = {
  'East US 2':      { rg: 'ROSSSAASPUBLIC',            vnet: 'RossSaaSProd',                sql: 'sqlsrv-eastus2-prd-ross-001' },
  'Central US':     { rg: 'Ross_Saas_DR_RG',           vnet: 'Ross_SaaS_DR_VNet',           sql: 'sqlsrv-centralus-prd-db-001' },
  'West US':        { rg: 'RG-westus-prd-ross',         vnet: 'vnet-westus-prd-ross',         sql: 'sqlsrv-westus-prd-001' },
  'North EU':       { rg: 'rg-northeu-prd-ross',        vnet: 'vnet-northeu-prd-ross',        sql: 'sqlsrv-northeu-prd-ross-001' },
  'West EU':        { rg: 'rg-westeu-prd-ross',         vnet: 'vnet-westeu-prd-ross',         sql: null },
  'Canada Central': { rg: 'Rg-canadacentral-prd-ross',  vnet: 'vnet-canadacentral-prd-ross',  sql: 'sqlsrv-canadacentral-prd-ross-001' },
  'Canada East':    { rg: 'Rg-canadaeast-prd-ross',     vnet: 'vnet-canadaeast-prd-ross',     sql: null },
}

// Dev/test environments — only Central US is provisioned; other regions show no auto-values
const REGION_MAP_DEV: Record<string, { rg: string; vnet: string; sql: string | null }> = {
  'Central US': { rg: 'rg-centralus-tst-ross', vnet: 'vnet-centralus-tst-ross01', sql: 'sqlsrv-centralus-tst-db-001' },
}

const NON_PROD_ENVS = new Set(['dev', 'qa', 'uat', 'demo'])

function getRegionMap(env: string) {
  return NON_PROD_ENVS.has(env) ? REGION_MAP_DEV : REGION_MAP_PROD
}

const REGIONS = Object.keys(REGION_MAP_PROD)

const TSHIRT_RULES: Array<{ max: number; size: string; cpu: string; ram: string; sku: string }> = [
  { max: 50,       size: 'S',  cpu: '4 vCPU',  ram: '16 GB',  sku: 'D4s_v4'  },
  { max: 100,      size: 'M',  cpu: '8 vCPU',  ram: '32 GB',  sku: 'D8s_v4'  },
  { max: 250,      size: 'L',  cpu: '16 vCPU', ram: '64 GB',  sku: 'D16s_v4' },
  { max: Infinity, size: 'XL', cpu: '32 vCPU', ram: '128 GB', sku: 'D32s_v4' },
]

const ENVIRONMENTS = ['dev', 'qa', 'uat', 'demo', 'production']

const APPLICATION_ROLES = ['web', 'api', 'worker', 'batch', 'integration', 'reporting']

const DATABASE_SKUS = [
  { value: 'GP_Gen5_2',  label: 'General Purpose · 2 vCores' },
  { value: 'GP_Gen5_4',  label: 'General Purpose · 4 vCores' },
  { value: 'GP_Gen5_8',  label: 'General Purpose · 8 vCores' },
  { value: 'BC_Gen5_4',  label: 'Business Critical · 4 vCores' },
  { value: 'BC_Gen5_8',  label: 'Business Critical · 8 vCores' },
  { value: 'HS_Gen5_4',  label: 'Hyperscale · 4 vCores' },
]

const VM_ROLES = ['app', 'web', 'db', 'jump', 'integration', 'utility']

const OS_TYPES = [
  { value: 'windows-2022', label: 'Windows Server 2022' },
  { value: 'windows-2019', label: 'Windows Server 2019' },
  { value: 'ubuntu-2204',  label: 'Ubuntu 22.04 LTS' },
  { value: 'ubuntu-2004',  label: 'Ubuntu 20.04 LTS' },
  { value: 'rhel-9',       label: 'Red Hat Enterprise Linux 9' },
]

// ─── Types ──────────────────────────────────────────────────────────────────

interface Tag {
  key: string
  value: string
}

interface OnboardingForm {
  // Step 1
  productId: string
  productName: string
  environment: string
  userCount: string
  applicationRole: string
  tags: Tag[]
  // Step 2 (auto-populated, read-only)
  tshirtSize: string
  cpu: string
  ram: string
  vmSku: string
  // Step 3
  subscription: string
  customerRegion: string
  resourceGroup: string
  // Step 4
  vnet: string
  subnet: string
  // Step 5
  databaseName: string
  sqlServer: string
  databaseSku: string
  databaseSizeGb: number
  // Step 6
  vmName: string
  vmRole: string
  osType: string
}

interface Subnet {
  name: string
  cidr: string
  available_ips: number
}

const initialForm: OnboardingForm = {
  productId: '', productName: '', environment: '', userCount: '', applicationRole: '', tags: [],
  tshirtSize: '', cpu: '', ram: '', vmSku: '',
  subscription: 'Ross-SAAS', customerRegion: '', resourceGroup: '',
  vnet: '', subnet: '',
  databaseName: '', sqlServer: '', databaseSku: 'GP_Gen5_4', databaseSizeGb: 100,
  vmName: '', vmRole: '', osType: '',
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function computeSizing(userCount: string): Pick<OnboardingForm, 'tshirtSize' | 'cpu' | 'ram' | 'vmSku'> {
  const n = parseInt(userCount, 10)
  if (!userCount || Number.isNaN(n) || n <= 0) {
    return { tshirtSize: '', cpu: '', ram: '', vmSku: '' }
  }
  const rule = TSHIRT_RULES.find((r) => n <= r.max) ?? TSHIRT_RULES[TSHIRT_RULES.length - 1]
  return { tshirtSize: rule.size, cpu: rule.cpu, ram: rule.ram, vmSku: rule.sku }
}

function sizeBadgeClass(size: string): string {
  switch (size) {
    case 'S':  return styles.sizeBadgeS
    case 'M':  return styles.sizeBadgeM
    case 'L':  return styles.sizeBadgeL
    case 'XL': return styles.sizeBadgeXL
    default:   return styles.sizeBadgeM
  }
}

function buildPayload(form: OnboardingForm) {
  return {
    product: {
      id: form.productId,
      name: form.productName,
      environment: form.environment,
      user_count: parseInt(form.userCount, 10) || 0,
      application_role: form.applicationRole,
      tags: form.tags.filter((t) => t.key.trim() !== ''),
    },
    sizing: {
      tshirt_size: form.tshirtSize,
      cpu: form.cpu,
      ram: form.ram,
      vm_sku: form.vmSku,
    },
    placement: {
      subscription: form.subscription,
      region: form.customerRegion,
      resource_group: form.resourceGroup,
    },
    networking: {
      vnet: form.vnet,
      subnet: form.subnet,
    },
    database: {
      name: form.databaseName,
      sql_server: form.sqlServer,
      sku: form.databaseSku,
      size_gb: form.databaseSizeGb,
    },
    virtual_machine: {
      name: form.vmName,
      role: form.vmRole,
      os_type: form.osType,
    },
  }
}

// ─── Field Component ──────────────────────────────────────────────────────────

function Field({
  label, required, error, children, full, badge,
}: {
  label: string
  required?: boolean
  error?: string
  children: React.ReactNode
  full?: boolean
  badge?: string
}) {
  return (
    <div className={full ? `${styles.formGroup} ${styles.formGroupFull}` : styles.formGroup}>
      <label className={styles.formLabel}>
        {label} {required && <span className={styles.requiredMark}>*</span>}
        {badge && <span className={styles.autoPopBadge}>{badge}</span>}
      </label>
      {children}
      {error && <span className={styles.errorMsg}>{error}</span>}
    </div>
  )
}

// ─── Main Component ─────────────────────────────────────────────────────────

export default function ProductOnboardingPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [form, setForm] = useState<OnboardingForm>(initialForm)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [showErrors, setShowErrors] = useState(false)
  const [copied, setCopied] = useState(false)

  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [requestId, setRequestId] = useState<string | null>(null)

  const { data: sizeConfigs } = useSizeConfigs()

  // Look up admin-configured size spec for the current product + t-shirt size
  const adminSizeConfig = sizeConfigs?.find(
    (sc) => sc.product_name.toLowerCase() === 'ross' && sc.size === form.tshirtSize
  )

  // Live Azure VM pricing — keyed by t-shirt size, product-aware so SKU comes from admin
  const { data: azurePricing } = useAzurePricing(
    form.customerRegion || 'Central US',
    'ross'
  )

  // Number of VMs = min_replicas from admin config (default 3 for Ross)
  const vmCount = adminSizeConfig?.resources.min_replicas ?? 3
  // Per-VM cost: prefer live Azure price, fall back to admin estimated cost
  const pricePerVm = azurePricing?.[form.tshirtSize]?.monthly_estimate
    || adminSizeConfig?.resources.estimated_monthly_cost_usd
    || 0
  const totalVmCost = pricePerVm * vmCount
  const ipCostMonthly = Math.round(vmCount * 3.65)
  const grandTotalCost = totalVmCost + ipCostMonthly

  // ── Auto-calculate sizing when userCount changes ──────────────────────────
  useEffect(() => {
    const sizing = computeSizing(form.userCount)
    setForm((f) => {
      if (
        f.tshirtSize === sizing.tshirtSize &&
        f.cpu === sizing.cpu &&
        f.ram === sizing.ram &&
        f.vmSku === sizing.vmSku
      ) {
        return f
      }
      return { ...f, ...sizing }
    })
  }, [form.userCount])

  // ── Fetch subnets for selected region/vnet ────────────────────────────────
  const subnetsQuery = useQuery({
    queryKey: ['onboarding-subnets', form.customerRegion, form.vnet],
    queryFn: async () => {
      const res = await api.get<Subnet[]>('/onboarding/subnets/', {
        params: { region: form.customerRegion, vnet: form.vnet },
      })
      return res.data
    },
    enabled: !!form.customerRegion,
  })

  const availableSubnets = (subnetsQuery.data ?? []).filter((s) => s.available_ips > 50)

  // ── Field setters ──────────────────────────────────────────────────────────
  function update<K extends keyof OnboardingForm>(key: K, value: OnboardingForm[K]) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  function handleRegionChange(region: string) {
    const map = getRegionMap(form.environment)[region]
    setForm((f) => ({
      ...f,
      customerRegion: region,
      resourceGroup: map?.rg ?? '',
      vnet: map?.vnet ?? '',
      sqlServer: map?.sql ?? '',
      subnet: '',
    }))
  }

  // Re-derive network values when environment changes (prod ↔ dev/test maps differ)
  useEffect(() => {
    if (!form.customerRegion) return
    const map = getRegionMap(form.environment)[form.customerRegion]
    setForm((f) => ({
      ...f,
      resourceGroup: map?.rg ?? '',
      vnet: map?.vnet ?? '',
      sqlServer: map?.sql ?? '',
      subnet: '',
    }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.environment])

  // ── Tags ───────────────────────────────────────────────────────────────────
  function addTag() {
    setForm((f) => ({ ...f, tags: [...f.tags, { key: '', value: '' }] }))
  }
  function updateTag(index: number, field: keyof Tag, value: string) {
    setForm((f) => {
      const tags = f.tags.map((t, i) => (i === index ? { ...t, [field]: value } : t))
      return { ...f, tags }
    })
  }
  function removeTag(index: number) {
    setForm((f) => ({ ...f, tags: f.tags.filter((_, i) => i !== index) }))
  }

  // ── Validation ──────────────────────────────────────────────────────────────
  function validateStep(s: number): Record<string, string> {
    const e: Record<string, string> = {}
    if (s === 1) {
      if (!form.productId.trim())       e.productId = 'Product ID is required'
      if (!form.productName.trim())     e.productName = 'Product name is required'
      if (!form.environment)            e.environment = 'Environment is required'
      if (!form.userCount.trim())       e.userCount = 'User count is required'
      else if (Number.isNaN(parseInt(form.userCount, 10)) || parseInt(form.userCount, 10) <= 0) {
        e.userCount = 'Enter a valid user count'
      }
      if (!form.applicationRole)        e.applicationRole = 'Application role is required'
    } else if (s === 3) {
      if (!form.customerRegion)         e.customerRegion = 'Region is required'
    } else if (s === 4) {
      if (!form.vnet.trim())            e.vnet = 'VNet is required'
      if (!form.subnet)                 e.subnet = 'Subnet is required'
    } else if (s === 5) {
      if (!form.databaseName.trim())    e.databaseName = 'Database name is required'
      if (!form.databaseSku)            e.databaseSku = 'Database SKU is required'
    } else if (s === 6) {
      if (!form.vmName.trim())          e.vmName = 'VM name is required'
      if (!form.vmRole)                 e.vmRole = 'VM role is required'
      if (!form.osType)                 e.osType = 'OS type is required'
    }
    return e
  }

  function goNext() {
    const e = validateStep(step)
    setErrors(e)
    if (Object.keys(e).length > 0) {
      setShowErrors(true)
      return
    }
    setShowErrors(false)
    setStep((s) => Math.min(s + 1, STEPS.length))
  }

  function goBack() {
    setShowErrors(false)
    setErrors({})
    setStep((s) => Math.max(s - 1, 1))
  }

  function goToStep(target: number) {
    // Allow navigating to any prior step freely; for forward jumps, validate intervening steps.
    if (target <= step) {
      setShowErrors(false)
      setErrors({})
      setStep(target)
      return
    }
    for (let s = step; s < target; s++) {
      const e = validateStep(s)
      if (Object.keys(e).length > 0) {
        setStep(s)
        setErrors(e)
        setShowErrors(true)
        return
      }
    }
    setShowErrors(false)
    setErrors({})
    setStep(target)
  }

  const payload = buildPayload(form)

  function copyJson() {
    navigator.clipboard.writeText(JSON.stringify(payload, null, 2)).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  async function handleSubmit() {
    setSubmitting(true)
    setSubmitError(null)
    try {
      const res = await api.post<{ request_id?: string; id?: string }>('/onboarding/requests/', payload)
      const id = res.data?.request_id ?? res.data?.id ?? 'PENDING'
      setRequestId(id)
    } catch (err: any) {
      setSubmitError(
        err?.response?.data?.detail ??
        err?.message ??
        'Failed to submit provisioning request. Please try again.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  function resetForm() {
    setForm(initialForm)
    setErrors({})
    setShowErrors(false)
    setRequestId(null)
    setSubmitError(null)
    setStep(1)
  }

  const err = (key: string) => (showErrors ? errors[key] : undefined)

  // ── Success state ─────────────────────────────────────────────────────────
  if (requestId) {
    return (
      <div className={styles.page}>
        <div className={styles.successPanel}>
          <div className={styles.successIcon}>
            <CheckCircle size={32} />
          </div>
          <h2 className={styles.successTitle}>Provisioning Request Submitted</h2>
          <div className={styles.requestIdChip}>{requestId}</div>
          <span className={styles.statusBadge}>Submitted</span>
          <p className={styles.successDesc}>
            Your provisioning request has been submitted. You will receive email and Teams
            notifications as the deployment progresses.
          </p>
          <div className={styles.footerRight}>
            <button className={styles.btnSecondary} onClick={resetForm}>
              Submit Another
            </button>
            <button className={styles.btnPrimary} onClick={() => navigate('/deployments')}>
              View Status Dashboard
            </button>
          </div>
        </div>
      </div>
    )
  }

  const progressPct = (step / STEPS.length) * 100

  return (
    <div className={styles.page}>
      <div className={styles.progressBar} style={{ width: `${progressPct}%` }} />

      {/* Header */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.headerTitle}>Product Onboarding</h1>
          <p className={styles.headerDesc}>
            Provision a new product environment across Azure infrastructure.
          </p>
        </div>
        <div className={styles.stepChip}>
          Step {step} of {STEPS.length}
        </div>
      </div>

      <div className={styles.body}>
        {/* Stepper sidebar */}
        <nav className={styles.stepper}>
          {STEPS.map((s, idx) => {
            const isActive = s.number === step
            const isDone = s.number < step
            return (
              <div key={s.number}>
                <button
                  type="button"
                  className={[
                    styles.stepperItem,
                    isActive && styles.stepperItemActive,
                    isDone && styles.stepperItemDone,
                  ].filter(Boolean).join(' ')}
                  onClick={() => goToStep(s.number)}
                >
                  <span
                    className={[
                      styles.stepperNum,
                      isActive && styles.stepperNumActive,
                      isDone && styles.stepperNumDone,
                    ].filter(Boolean).join(' ')}
                  >
                    {isDone ? <Check size={15} /> : s.number}
                  </span>
                  <span className={styles.stepperText}>
                    <span className={styles.stepperLabel}>{s.label}</span>
                    <span className={styles.stepperDesc}>{s.desc}</span>
                  </span>
                </button>
                {idx < STEPS.length - 1 && <span className={styles.stepConnector} />}
              </div>
            )
          })}
        </nav>

        {/* Content */}
        <div className={styles.content}>
          <div className={styles.stepCard}>
            {/* ── Step 1: Product Information ──────────────────────────────── */}
            {step === 1 && (
              <>
                <h2 className={styles.stepTitle}>
                  <Package size={18} /> Product Information
                </h2>
                <p className={styles.stepSubtitle}>
                  Identify the product, target environment, and expected scale.
                </p>
                <div className={styles.formGrid}>
                  <Field label="Product ID" required error={err('productId')}>
                    <input
                      className={`${styles.formInput} ${err('productId') ? styles.inputError : ''}`}
                      value={form.productId}
                      placeholder="e.g. ross-erp"
                      onChange={(e) => update('productId', e.target.value)}
                    />
                  </Field>
                  <Field label="Product Name" required error={err('productName')}>
                    <input
                      className={`${styles.formInput} ${err('productName') ? styles.inputError : ''}`}
                      value={form.productName}
                      placeholder="e.g. Ross ERP"
                      onChange={(e) => update('productName', e.target.value)}
                    />
                  </Field>
                  <Field label="Environment" required error={err('environment')}>
                    <select
                      className={`${styles.formSelect} ${err('environment') ? styles.inputError : ''}`}
                      value={form.environment}
                      onChange={(e) => update('environment', e.target.value)}
                    >
                      <option value="">Select environment…</option>
                      {ENVIRONMENTS.map((e) => (
                        <option key={e} value={e}>{e.toUpperCase()}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Expected User Count" required error={err('userCount')}>
                    <input
                      type="number"
                      min={1}
                      className={`${styles.formInput} ${err('userCount') ? styles.inputError : ''}`}
                      value={form.userCount}
                      placeholder="e.g. 120"
                      onChange={(e) => update('userCount', e.target.value)}
                    />
                  </Field>
                  <Field label="Application Role" required error={err('applicationRole')} full>
                    <select
                      className={`${styles.formSelect} ${err('applicationRole') ? styles.inputError : ''}`}
                      value={form.applicationRole}
                      onChange={(e) => update('applicationRole', e.target.value)}
                    >
                      <option value="">Select role…</option>
                      {APPLICATION_ROLES.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </Field>
                </div>

                {/* Tags */}
                <div className={styles.formGroupFull}>
                  <label className={styles.formLabel}>Tags</label>
                  <div className={styles.tagList}>
                    {form.tags.length === 0 && (
                      <p className={styles.emptyHint}>No tags added yet.</p>
                    )}
                    {form.tags.map((tag, i) => (
                      <div key={i} className={styles.tagRow}>
                        <input
                          className={styles.tagInput}
                          placeholder="key"
                          value={tag.key}
                          onChange={(e) => updateTag(i, 'key', e.target.value)}
                        />
                        <span className={styles.tagSep}>=</span>
                        <input
                          className={styles.tagInput}
                          placeholder="value"
                          value={tag.value}
                          onChange={(e) => updateTag(i, 'value', e.target.value)}
                        />
                        <button
                          type="button"
                          className={styles.tagDelete}
                          onClick={() => removeTag(i)}
                          aria-label="Remove tag"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    ))}
                    <button type="button" className={styles.addTagBtn} onClick={addTag}>
                      <Plus size={14} /> Add Tag
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* ── Step 2: Infrastructure Sizing ───────────────────────────── */}
            {step === 2 && (
              <>
                <h2 className={styles.stepTitle}>
                  <Settings size={18} /> Infrastructure Sizing
                </h2>
                <p className={styles.stepSubtitle}>
                  Compute tier is automatically derived from the expected user count.
                </p>

                {!form.tshirtSize ? (
                  <div className={styles.infoBox}>
                    <Info size={16} />
                    Enter an expected user count in Step 1 to calculate the recommended sizing.
                  </div>
                ) : (
                  <>
                    <div className={styles.infoBox}>
                      <Info size={16} />
                      Based on <strong>{form.userCount}</strong> expected users, we recommend the{' '}
                      <strong>{form.tshirtSize}</strong> tier.
                    </div>
                    <div className={styles.sizeCard}>
                      <div className={styles.sizeCardItem}>
                        <span className={styles.sizeCardLabel}>T-Shirt Size</span>
                        <span className={sizeBadgeClass(form.tshirtSize)}>{form.tshirtSize}</span>
                      </div>
                      <div className={styles.sizeCardItem}>
                        <span className={styles.sizeCardLabel}>vCPU</span>
                        <span className={styles.sizeCardValue}>
                          {adminSizeConfig ? adminSizeConfig.resources.cpu_request : form.cpu}
                        </span>
                      </div>
                      <div className={styles.sizeCardItem}>
                        <span className={styles.sizeCardLabel}>Memory</span>
                        <span className={styles.sizeCardValue}>
                          {adminSizeConfig ? adminSizeConfig.resources.memory_request : form.ram}
                        </span>
                      </div>
                      <div className={styles.sizeCardItem}>
                        <span className={styles.sizeCardLabel}>VM SKU</span>
                        <span className={styles.sizeCardValue}>
                          {adminSizeConfig?.resources.storage_class || form.vmSku || '—'}
                        </span>
                      </div>
                      {pricePerVm > 0 && (
                        <div style={{ gridColumn: '1/-1', borderTop: '1px solid var(--aptean-border)', paddingTop: 8, marginTop: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <div className={styles.sizeCardItem}>
                            <span className={styles.sizeCardLabel}>{vmCount} VMs ({vmCount} × ${pricePerVm.toLocaleString(undefined, { maximumFractionDigits: 0 })})</span>
                            <span className={styles.sizeCardValue}>${totalVmCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo</span>
                          </div>
                          <div className={styles.sizeCardItem}>
                            <span className={styles.sizeCardLabel}>Public IPs ({vmCount} × $3.65)</span>
                            <span className={styles.sizeCardValue}>~${ipCostMonthly.toLocaleString()}/mo</span>
                          </div>
                          <div className={styles.sizeCardItem} style={{ borderTop: '1px solid var(--aptean-border)', paddingTop: 6, marginTop: 2 }}>
                            <span className={styles.sizeCardLabel} style={{ fontWeight: 600 }}>Est. Total</span>
                            <span className={styles.sizeCardValue} style={{ fontWeight: 700, color: 'var(--aptean-teal)' }}>
                              ${grandTotalCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </>
            )}

            {/* ── Step 3: Resource Group & Subscription ───────────────────── */}
            {step === 3 && (
              <>
                <h2 className={styles.stepTitle}>
                  <Server size={18} /> Resource Group & Subscription
                </h2>
                <p className={styles.stepSubtitle}>
                  Choose the customer region. Resource group placement is derived automatically.
                </p>
                <div className={styles.formGrid}>
                  <Field label="Subscription" full>
                    <input className={styles.formInput} value={form.subscription} readOnly />
                  </Field>
                  <Field label="Customer Region" required error={err('customerRegion')}>
                    <select
                      className={`${styles.formSelect} ${err('customerRegion') ? styles.inputError : ''}`}
                      value={form.customerRegion}
                      onChange={(e) => handleRegionChange(e.target.value)}
                    >
                      <option value="">Select region…</option>
                      {REGIONS.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Resource Group" badge={form.resourceGroup ? 'Auto' : undefined}>
                    <input className={styles.formInput} value={form.resourceGroup} readOnly placeholder="—" />
                  </Field>
                </div>
                {form.customerRegion && (
                  <div className={styles.infoBox}>
                    <Info size={16} />
                    Region <strong>{form.customerRegion}</strong> maps to resource group{' '}
                    <strong>{form.resourceGroup}</strong>.
                  </div>
                )}
              </>
            )}

            {/* ── Step 4: Networking Configuration ────────────────────────── */}
            {step === 4 && (
              <>
                <h2 className={styles.stepTitle}>
                  <Network size={18} /> Networking Configuration
                </h2>
                <p className={styles.stepSubtitle}>
                  The VNet is pre-selected from the region. Choose an available subnet.
                </p>
                <div className={styles.formGrid}>
                  <Field label="Virtual Network" required error={err('vnet')} badge={form.vnet ? 'Auto' : undefined}>
                    <input
                      className={`${styles.formInput} ${err('vnet') ? styles.inputError : ''}`}
                      value={form.vnet}
                      readOnly
                      placeholder="Select a region first"
                    />
                  </Field>
                  <Field label="Subnet" required error={err('subnet')}>
                    <select
                      className={`${styles.formSelect} ${err('subnet') ? styles.inputError : ''}`}
                      value={form.subnet}
                      onChange={(e) => update('subnet', e.target.value)}
                      disabled={!form.customerRegion || subnetsQuery.isLoading}
                    >
                      <option value="">
                        {subnetsQuery.isLoading ? 'Loading subnets…' : 'Select subnet…'}
                      </option>
                      {availableSubnets.map((s) => (
                        <option key={s.name} value={s.name} className={styles.subnetOption}>
                          {s.name} ({s.cidr} — {s.available_ips} IPs available)
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
                {subnetsQuery.isError && (
                  <div className={styles.errorBox}>
                    <AlertCircle size={16} />
                    Failed to load subnets for this region.
                  </div>
                )}
                {!subnetsQuery.isLoading && !subnetsQuery.isError && form.customerRegion && availableSubnets.length === 0 && (
                  <div className={styles.infoBox}>
                    <Info size={16} />
                    No subnets with sufficient capacity (&gt;50 available IPs) found for this VNet.
                  </div>
                )}
              </>
            )}

            {/* ── Step 5: Database Configuration ──────────────────────────── */}
            {step === 5 && (
              <>
                <h2 className={styles.stepTitle}>
                  <Database size={18} /> Database Configuration
                </h2>
                <p className={styles.stepSubtitle}>
                  Provision an Azure SQL database. The server is derived from the selected region.
                </p>
                <div className={styles.formGrid}>
                  <Field label="Database Name" required error={err('databaseName')}>
                    <input
                      className={`${styles.formInput} ${err('databaseName') ? styles.inputError : ''}`}
                      value={form.databaseName}
                      placeholder="e.g. ross_prod_db"
                      onChange={(e) => update('databaseName', e.target.value)}
                    />
                  </Field>
                  <Field label="SQL Server" badge={form.sqlServer ? 'Auto' : undefined}>
                    <input
                      className={styles.formInput}
                      value={form.sqlServer}
                      readOnly
                      placeholder="No SQL server for this region"
                    />
                  </Field>
                  <Field label="Database SKU" required error={err('databaseSku')}>
                    <select
                      className={`${styles.formSelect} ${err('databaseSku') ? styles.inputError : ''}`}
                      value={form.databaseSku}
                      onChange={(e) => update('databaseSku', e.target.value)}
                    >
                      {DATABASE_SKUS.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Database Size (GB)">
                    <input
                      type="number"
                      min={1}
                      className={styles.formInput}
                      value={form.databaseSizeGb}
                      onChange={(e) => update('databaseSizeGb', parseInt(e.target.value, 10) || 0)}
                    />
                  </Field>
                </div>
                {!form.sqlServer && form.customerRegion && (
                  <div className={styles.infoBox}>
                    <Info size={16} />
                    No managed SQL server is mapped to <strong>{form.customerRegion}</strong>. A new
                    server will be provisioned as part of this request.
                  </div>
                )}
              </>
            )}

            {/* ── Step 6: Virtual Machine Configuration ───────────────────── */}
            {step === 6 && (
              <>
                <h2 className={styles.stepTitle}>
                  <Server size={18} /> Virtual Machine Configuration
                </h2>
                <p className={styles.stepSubtitle}>
                  Define the VM identity and operating system. Compute size is set by the sizing tier.
                </p>
                <div className={styles.formGrid}>
                  <Field label="VM Name" required error={err('vmName')}>
                    <input
                      className={`${styles.formInput} ${err('vmName') ? styles.inputError : ''}`}
                      value={form.vmName}
                      placeholder="e.g. vm-ross-prd-app-01"
                      onChange={(e) => update('vmName', e.target.value)}
                    />
                  </Field>
                  <Field label="VM Role" required error={err('vmRole')}>
                    <select
                      className={`${styles.formSelect} ${err('vmRole') ? styles.inputError : ''}`}
                      value={form.vmRole}
                      onChange={(e) => update('vmRole', e.target.value)}
                    >
                      <option value="">Select role…</option>
                      {VM_ROLES.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="OS Type" required error={err('osType')}>
                    <select
                      className={`${styles.formSelect} ${err('osType') ? styles.inputError : ''}`}
                      value={form.osType}
                      onChange={(e) => update('osType', e.target.value)}
                    >
                      <option value="">Select OS…</option>
                      {OS_TYPES.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="VM SKU" badge={form.vmSku ? 'Auto' : undefined}>
                    <input className={styles.formInput} value={form.vmSku} readOnly placeholder="—" />
                  </Field>
                </div>
              </>
            )}

            {/* ── Step 7: Review & Submit ─────────────────────────────────── */}
            {step === 7 && (
              <>
                <h2 className={styles.stepTitle}>
                  <FileText size={18} /> Review & Submit
                </h2>
                <p className={styles.stepSubtitle}>
                  Confirm the configuration below. Submitting will queue the provisioning request.
                </p>

                {/* Product */}
                <div className={styles.reviewSection}>
                  <h3 className={styles.reviewSectionTitle}>
                    <Package size={14} /> Product Information
                  </h3>
                  <ReviewRow k="Product ID" v={form.productId} />
                  <ReviewRow k="Product Name" v={form.productName} />
                  <ReviewRow k="Environment" v={form.environment} />
                  <ReviewRow k="Expected Users" v={form.userCount} />
                  <ReviewRow k="Application Role" v={form.applicationRole} />
                  <ReviewRow
                    k="Tags"
                    v={
                      form.tags.filter((t) => t.key.trim()).length
                        ? form.tags.filter((t) => t.key.trim()).map((t) => `${t.key}=${t.value}`).join(', ')
                        : '—'
                    }
                  />
                </div>

                {/* Sizing */}
                <div className={styles.reviewSection}>
                  <h3 className={styles.reviewSectionTitle}>
                    <Settings size={14} /> Infrastructure Sizing
                  </h3>
                  <ReviewRow k="T-Shirt Size" v={form.tshirtSize} />
                  <ReviewRow k="vCPU" v={form.cpu} />
                  <ReviewRow k="Memory" v={form.ram} />
                  <ReviewRow k="VM SKU" v={form.vmSku} />
                </div>

                {/* Placement */}
                <div className={styles.reviewSection}>
                  <h3 className={styles.reviewSectionTitle}>
                    <Server size={14} /> Resource Group & Subscription
                  </h3>
                  <ReviewRow k="Subscription" v={form.subscription} />
                  <ReviewRow k="Region" v={form.customerRegion} />
                  <ReviewRow k="Resource Group" v={form.resourceGroup} />
                </div>

                {/* Networking */}
                <div className={styles.reviewSection}>
                  <h3 className={styles.reviewSectionTitle}>
                    <Network size={14} /> Networking
                  </h3>
                  <ReviewRow k="Virtual Network" v={form.vnet} />
                  <ReviewRow k="Subnet" v={form.subnet} />
                </div>

                {/* Database */}
                <div className={styles.reviewSection}>
                  <h3 className={styles.reviewSectionTitle}>
                    <Database size={14} /> Database
                  </h3>
                  <ReviewRow k="Database Name" v={form.databaseName} />
                  <ReviewRow k="SQL Server" v={form.sqlServer || '— (new server)'} />
                  <ReviewRow k="SKU" v={form.databaseSku} />
                  <ReviewRow k="Size" v={`${form.databaseSizeGb} GB`} />
                </div>

                {/* VM */}
                <div className={styles.reviewSection}>
                  <h3 className={styles.reviewSectionTitle}>
                    <Server size={14} /> Virtual Machine
                  </h3>
                  <ReviewRow k="VM Name" v={form.vmName} />
                  <ReviewRow k="VM Role" v={form.vmRole} />
                  <ReviewRow k="OS Type" v={form.osType} />
                </div>

                {/* JSON Payload */}
                <div className={styles.reviewSection}>
                  <h3 className={styles.reviewSectionTitle}>
                    <FileText size={14} /> Payload Preview
                    <button type="button" className={styles.copyBtn} onClick={copyJson}>
                      {copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy JSON</>}
                    </button>
                  </h3>
                  <pre className={styles.payloadPre}>{JSON.stringify(payload, null, 2)}</pre>
                </div>

                {submitError && (
                  <div className={styles.errorBox}>
                    <AlertCircle size={16} />
                    {submitError}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Footer / Nav */}
      <div className={styles.footer}>
        <div className={styles.footerLeft}>
          {step > 1 && (
            <button type="button" className={styles.btnSecondary} onClick={goBack}>
              <ChevronLeft size={16} /> Back
            </button>
          )}
        </div>
        <div className={styles.footerRight}>
          <button type="button" className={styles.btnGhost} onClick={() => navigate('/deployments')}>
            Cancel
          </button>
          {step < STEPS.length ? (
            <button type="button" className={styles.btnPrimary} onClick={goNext}>
              Next <ChevronRight size={16} />
            </button>
          ) : (
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Submitting…' : <><Rocket size={16} /> Submit Request</>}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Review Row helper ──────────────────────────────────────────────────────

function ReviewRow({ k, v }: { k: string; v: string }) {
  return (
    <div className={styles.reviewRow}>
      <span className={styles.reviewKey}>{k}</span>
      <span className={styles.reviewVal}>{v || '—'}</span>
    </div>
  )
}
